/* Copyright (c) 2021 MOCA Systems, Inc.  All rights reserved. */

import { Injectable } from '@angular/core';
import { BehaviorSubject, Subject, Subscription, of, combineLatest, from, Observable } from 'rxjs';
import { takeUntil, map, switchMap, filter, take, distinctUntilChanged, tap } from 'rxjs/operators';

import { AngularFireAuth } from '@angular/fire/compat/auth';
import { AngularFireDatabase } from '@angular/fire/compat/database';

import { UserObject } from 'src/app/common/fancy-firebase/objects/UserObject';
import { UserIndexList, UserIndex } from 'src/app/common/fancy-firebase/lists/UserIndexList';
import { User } from '@touchplan/common';

import { FancyActionTrackerFactory } from '../common/fancy-firebase/services/fancy-action-tracker-factory.service';

// next things:
// - implement getting the user data from auth
// - use in account.profile to init and update form

import { CoreState } from 'src/app/app-data/interfaces/app-state.interface';
import { fancyFirebaseGetters, fancyFirebaseReferencer } from '@touchplan/fancy-referencer';
import { LoggerServiceFactory } from '../common/logging/LoggerServiceFactory';
import { ILoggerService } from '@touchplan/logging';
import { isMorphedUser } from '../utils/token-util';
import { UserIdTpData } from '../common/injectable-data/user-id';

import firebase from 'firebase/compat/app';

export interface AppStateUser extends User {
	indices: Map<string, UserIndex>;
	morphedUser?: boolean;
}

@Injectable({
	providedIn: 'root',
})
export class AppState implements CoreState {
	private shutdownSubject = new Subject<void>();
	private unsubscribeList: Subscription[];
	private angularLand = {
		angularFireDatabase: this.angularFireDatabase,
		fancyActionTrackerFactory: this.fancyActionTrackerFactory,
	};

	/**
		emits user data for the currently authenticated users
		Note: every emission is always a new object
	*/
	public user: BehaviorSubject<AppStateUser> = new BehaviorSubject(null);
	public userSync: AppStateUser;

	/**
	 * Cold observable variant of user that does not emit until the authUser is known
	 * Use in scenarios where stalling the first emission until user has loaded
	 * is more important than an initial sync emission.
	 */
	public waitForUser$ = this._waitForUser$();

	// This yields just the current user's ID.  Duplicates and nulls (falsey values) are filtered out as well.
	public userId: Observable<string> = this.user.pipe(
		map((user) => (user ? user.$id : null)),
		filter((userId) => !!userId),
		distinctUntilChanged()
	);

	private isMorphedUser$: Observable<boolean> = this.angularFireAuth.idToken.pipe(
		map((token: string) => isMorphedUser(token)),
		distinctUntilChanged()
	);

	private logger: ILoggerService;

	constructor(
		public angularFireAuth: AngularFireAuth,
		private angularFireDatabase: AngularFireDatabase,
		private fancyActionTrackerFactory: FancyActionTrackerFactory,
		private userIdTpData: UserIdTpData<string>
	) {
		window.appState = this;
		this.logger = LoggerServiceFactory.create('AppState');
	}

	//see no real reason to bother passing in user data...
	public setup() {
		this.unsubscribeList = [];
		this.angularFireAuth.user
			.pipe(
				takeUntil(this.shutdownSubject),
				// tap(authUser => this.logger.log('%c PRE  authUser', 'background: #FFBBBB', authUser)),
				distinctUntilChanged((prevAuthUser, currAuthUser) => {
					if (!currAuthUser) {
						return prevAuthUser === currAuthUser;
					}
					// this.logger.log(`%c authUser: prev-id: ${prevAuthUser?.uid}, curr-id: ${currAuthUser.uid}`, 'background: #FFBBBB');
					return prevAuthUser && prevAuthUser.uid === currAuthUser.uid;
				}),
				// tap(authUser => this.logger.log('%c POST authUser', 'background: #FFBBBB', authUser)),
				switchMap((authUser) => {
					if (!authUser) {
						return of(null);
					}
					const userPath = fancyFirebaseGetters.getUsersPath(authUser.uid);
					const indicesUsersPath = fancyFirebaseGetters.getIndicesUsersPath(authUser.uid);
					const userObject = new UserObject(
						this.angularLand,
						fancyFirebaseReferencer.getLegacyRef<firebase.database.Reference>(userPath)
					);
					const userIndexList = new UserIndexList(
						this.angularLand,
						fancyFirebaseReferencer.getLegacyRef<firebase.database.Reference>(indicesUsersPath)
					);

					return combineLatest([userObject.object$, userIndexList.loadedList$, this.isMorphedUser$]).pipe(
						map(([userObject, indices, morphedUser]) => {
							if (userObject) {
								return {
									...userObject,
									indices,
									morphedUser,
								};
							}
							return userObject;
						})
					);
				})
			)
			.subscribe(this.user);

		this.user
			.pipe(
				filter((user) => !!user),
				takeUntil(this.shutdownSubject)
			)
			.subscribe((user) => {
				this.userSync = user;
			});

		this.userIdTpData.registerDataSource(this.userId.pipe(takeUntil(this.shutdownSubject)));
	}
	public boom() {
		this.shutdownSubject.next();
		this.unsubscribeList.forEach((sub) => sub.unsubscribe());
	}

	private _waitForUser$() {
		return this.angularFireAuth.user.pipe(
			//only care when this changes between null and not null states (and new users)
			distinctUntilChanged((x, y) => (!x && !!y) || (!!x && !y) || (!!x && !!y && x.uid !== y.uid)),
			switchMap((authUser) => (authUser ? this.user.pipe(filter((u) => !!u)) : of(null as AppStateUser)))
		);
	}
}
